import moment from 'moment';
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithErrorHandling from './baseQuery';
import { Constants } from '../constants/Constants';

const { BASE_API_URL } = Constants;

export interface HistoricalRequestBody {
    start_date: string | number;
    end_date: string | number;
    vehicles: string[];
    products: string[];
    ownership_consent: string;
}

export interface HistoricalRequestPresignedBody {
    request_id: string;
    download_path: string;
}

interface HistoricalResponse {
    mappedHistoricRequests: HistoricalRecord[];
    isLoading: boolean;
}

interface SignedUrlResponse {
    signed_url: string;
}

export interface HistoricalRecord {
    id: string;
    status: string;
    startDate: string;
    endDate: string;
    vehicles: Vehicle[];
    products: string[];
    downloadPaths?: string[];
    createDate: string;
}

export interface Vehicle {
    VIN: string;
    id: string;
    name: string;
}

interface HistoricalRequestResponse {
    request_id: string;
    create_date: string;
    status: string;
    download_paths: string[];
    start_date: string;
    end_date: string;
    vehicles: Vehicle[];
    products: string[];
}

export const historicalApi = createApi({
    reducerPath: 'historicalApi',
    baseQuery: baseQueryWithErrorHandling({ baseUrl: BASE_API_URL }),
    endpoints: builder => ({
        fetchHistorical: builder.mutation<HistoricalResponse, HistoricalRequestBody>({
            query: bodyData => ({
                url: '/historical',
                method: 'POST',
                body: bodyData,
            }),
        }),
        fetchSignedFile: builder.mutation<SignedUrlResponse, HistoricalRequestPresignedBody>({
            query: bodyData => ({
                url: '/historical/sign',
                method: 'POST',
                body: bodyData,
            }),
        }),
        fetchHistoricalRequests: builder.query<HistoricalResponse, void>({
            query: () => ({
                url: '/historical',
            }),
            transformResponse: async (
                response: HistoricalRequestResponse[] = [] as HistoricalRequestResponse[]
            ): Promise<HistoricalResponse> => {
                const mappedHistoricRequests =
                    (response ?? []).map(mapHistoricRequest).sort((a, b) => {
                        const dateA = moment(a.createDate, 'DD/MM/YYYY HH:mm').toDate();
                        const dateB = moment(b.createDate, 'DD/MM/YYYY HH:mm').toDate();
                        return dateB.getTime() - dateA.getTime();
                    }) || ([] as HistoricalRecord[]);
                return { mappedHistoricRequests, isLoading: false };
            },
        }),

        fetchMetadata: builder.mutation<Blob, string>({
            query: requestId => ({
                url: `/historical/metadata/${requestId}`,
                method: 'GET',
                responseHandler: async response => response.blob(),
            }),
        }),
    }),
});

export const {
    useFetchHistoricalMutation,
    useFetchHistoricalRequestsQuery,
    useFetchSignedFileMutation,
    useFetchMetadataMutation,
} = historicalApi;

const mapHistoricRequest = (item: HistoricalRequestResponse = {} as HistoricalRequestResponse): HistoricalRecord => {
    const formatDate = (date: string): string => {
        return moment(date).format('DD/MM/YYYY HH:mm');
    };

    if (Object.keys(item).length === 0) {
        return {} as HistoricalRecord;
    }
    const {
        download_paths: downloadPaths = [],
        status,
        start_date: startDate = '',
        end_date: endDate = '',
        vehicles = [] as Vehicle[],
        products = [],
        request_id: id,
        create_date: createDate,
    } = item || ({} as HistoricalRecord);
    return {
        id,
        status,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        vehicles: vehicles,
        products,
        downloadPaths,
        createDate: formatDate(createDate),
    };
};
